/**
 * MODULE: platform/browser/frontend/utils/browserLiveViewUrl.ts
 * PURPOSE: Pick iframe-safe live view URLs; detect proxied Steel paths.
 * PUBLIC_API: pickEmbedLiveViewUrl, pickFullscreenLiveViewUrl, isProxiedSteelLiveUrl, needsServerEmbedLiveUrlRefresh
 * AGENT_SAFETY: SAFE
 * RECENT: 2026-06-17 [2.2.1437] — Inspector screencast fallback when Session Player WebRTC fails.
 *   [2.2.1435] — Steel WebRTC Session Player (`/v1/sessions/debug`) embed; inspector URLs treated as legacy.
 */

/**
 * Fullscreen Session Inspector (`devtools-fullscreen/inspector.html`).
 * Do not treat embed `devtools/inspector.html` as fullscreen — its wss param may contain `/devtools/page/`.
 */
export function isFullscreenDevToolsLiveUrl(url: string): boolean {
  try {
    return new URL(url).pathname.toLowerCase().includes('devtools-fullscreen');
  } catch {
    return url.toLowerCase().includes('devtools-fullscreen');
  }
}

/** Legacy Steel admin shell — replaced by Session Player / inspector embed [2.2.1429]. */
export function isLegacySteelUiEmbedUrl(url: string): boolean {
  const raw = (url ?? '').trim();
  if (!raw) return false;
  try {
    const path = new URL(raw).pathname.toLowerCase();
    if (path.endsWith('/ui') || path.endsWith('/steel/ui')) return true;
    return false;
  } catch {
    return raw.toLowerCase().includes('/steel/ui') || /\/ui(\?|$)/i.test(raw);
  }
}

/** DevTools inspector on Steel proxy — replaced by WebRTC Session Player [2.2.1435]. */
export function isLegacySteelInspectorEmbedUrl(url: string): boolean {
  const raw = (url ?? '').trim().toLowerCase();
  if (!raw.includes('devtools/inspector')) return false;
  if (raw.includes('browserbase.com')) return false;
  return isProxiedSteelLiveUrl(raw) || /localhost:3030|127\.0\.0\.1:3030/i.test(raw);
}

/** Browserbase inspector embed — append navbar=false for bordered screencast. */
export function withInspectorEmbedParams(url: string): string {
  const raw = (url ?? '').trim();
  if (!raw || raw.includes('navbar=false') || !raw.includes('devtools/inspector')) return raw;
  const sep = raw.includes('?') ? '&' : '?';
  return `${raw}${sep}navbar=false`;
}

/** Steel WebRTC Session Player embed path. */
export function isSteelSessionPlayerUrl(url: string): boolean {
  const raw = (url ?? '').trim().toLowerCase();
  return raw.includes('/sessions/debug');
}

/** Resolve iframe URL — player, inspector fallback, or manual screencast mode. */
export function resolveEmbedLiveViewUrl(
  embed?: string,
  fullscreen?: string,
  fallback?: string,
  mode: 'auto' | 'player' | 'inspector' = 'auto',
): string {
  const player = pickEmbedLiveViewUrl(embed, fullscreen);
  const inspector = withInspectorEmbedParams((fallback ?? '').trim());
  if (mode === 'inspector' && inspector) return inspector;
  if (mode === 'player' && player) return player;
  return player || inspector;
}

/** True when URL still contains Azure template placeholders (undeployed VM IP). */
export function isUnresolvedBrowserLiveUrl(url: string): boolean {
  const raw = (url ?? '').trim().toLowerCase();
  if (!raw) return true;
  return raw.includes('your_azure_dev_vm_ip') || raw.includes('<vm_ip>');
}

/** Prefer embed live view URL for iframe. */
export function pickEmbedLiveViewUrl(embed?: string, fullscreen?: string): string {
  const e = withInspectorEmbedParams((embed ?? '').trim());
  const f = withInspectorEmbedParams((fullscreen ?? '').trim());
  const embedOk = (url: string) =>
    url &&
    !isUnresolvedBrowserLiveUrl(url) &&
    !isFullscreenDevToolsLiveUrl(url) &&
    !isLegacySteelUiEmbedUrl(url) &&
    !isLegacySteelInspectorEmbedUrl(url);

  if (embedOk(e)) return e;
  if (embedOk(f)) return f;
  // Transient fallback until poll upgrades legacy inspector URLs [2.2.1435].
  if (
    e &&
    !isUnresolvedBrowserLiveUrl(e) &&
    !isFullscreenDevToolsLiveUrl(e) &&
    !isLegacySteelUiEmbedUrl(e)
  ) {
    return e;
  }
  return '';
}

export function pickFullscreenLiveViewUrl(embed?: string, fullscreen?: string): string {
  const f = (fullscreen ?? '').trim();
  const e = (embed ?? '').trim();
  return f || e;
}

/** Stored session URL should be refreshed from provider metadata. */
export function needsServerEmbedLiveUrlRefresh(url: string): boolean {
  const raw = (url ?? '').trim();
  if (!raw) return false;
  if (isLegacySteelUiEmbedUrl(raw)) return true;
  if (isLegacySteelInspectorEmbedUrl(raw)) return true;
  if (isFullscreenDevToolsLiveUrl(raw)) return true;
  return false;
}

/** True when live view is served via app Steel proxy (same-origin, low-latency). */
export function isProxiedSteelLiveUrl(url: string): boolean {
  const raw = (url ?? '').trim();
  if (!raw) return false;
  if (/localhost:3030|127\.0\.0\.1:3030/i.test(raw)) return false;
  try {
    const path = new URL(raw).pathname.toLowerCase();
    if (path === '/steel' || path.startsWith('/steel/')) return true;
    if (path.includes('/sessions/debug')) return true;
    if (path.startsWith('/devtools/')) return true;
    if (path.includes('/devtools/inspector')) return true;
    return false;
  } catch {
    return (
      raw.includes('/steel/') ||
      raw.endsWith('/steel') ||
      raw.includes('/sessions/debug') ||
      raw.includes('/devtools/inspector') ||
      raw.includes('/devtools/')
    );
  }
}
